import { Game, ListType, GameStatus } from "@/types/game";
import { getChargedAmount } from "@/utils/payment.utils";
import type { GameCardProps } from "@/components/layout/GameCard";
import type { StatusBadgeVariant } from "@/components/ui/status-badge";
import { buildCardCountdown } from "@/utils/vip-access.utils";
import {
  getSportCategoryLabel,
} from "@/utils/sport-categories";
import { BR_TIMEZONE, isoToYmdBr } from "@/utils/br-datetime";
import {
  canPerformEventSelfCheckIn,
  formatCheckInOpensIn,
  getCheckInWindowState,
  shouldShowEventCheckInPrompt,
} from "@/utils/event-check-in.utils";

export const PENDING_PAYMENT_STATUSES = [
  "PENDING_PAYMENT",
  "PENDING_PAYMENT_REVEAL",
  "PENDING_PAYMENT_WAITLIST",
  "PENDING_PAYMENT_PROMOTED",
] as const;
export const WAITING_STATUSES = ["WAITING", "WAITING_REVEAL"] as const;
export const VISIBLE_PENDING_PAYMENT_STATUSES = [
  "PENDING_PAYMENT_REVEAL",
  "PENDING_PAYMENT_WAITLIST",
] as const;

/**
 * Retorna o path da URL do jogo.
 * Usa shortCode para compartilhamento (link curto) quando disponível, senão usa id.
 */
export function getGamePath(
  game: { id: string; shortCode?: string } | string,
  subPath?: string,
): string {
  if (typeof game === "string") {
    return subPath ? `/games/${game}/${subPath}` : `/games/${game}`;
  }
  // Usar short link quando disponível (jogo e participantes)
  const useShort = !!game.shortCode;
  const base = useShort ? `/g/${game.shortCode}` : `/games/${game.id}`;
  return subPath ? `${base}/${subPath}` : base;
}

/** Página de check-in do jogo no dia (participante logado). */
export function getGameCheckInPath(
  game: { id: string; shortCode?: string } | string,
): string {
  return getGamePath(game, "check-in");
}

/**
 * Avaliações só podem ser enviadas ou alteradas no dia civil do jogo (Brasília).
 */
export function isGameRatingDayBr(
  gameDateIso: string | Date,
  now: Date = new Date(),
): boolean {
  return isoToYmdBr(gameDateIso) === isoToYmdBr(now);
}

/**
 * Quem pode abrir a avaliação pós-jogo (estrelas entre colegas de equipe sorteada).
 * No dia do jogo (para enviar/alterar) ou após encerrar (somente leitura).
 * Cancelado: nunca.
 * Participante confirmado, ou criador do jogo, ou ADMIN/OWNER do time (visão organizador).
 */
export function canRateTeamForGame(
  game: Game,
  options: {
    userId: string | undefined;
    teamUserRole?: string | null;
    /** Ex.: participante confirmado em "Meus jogos". */
    isParticipant?: boolean;
  },
): boolean {
  const { userId, teamUserRole, isParticipant } = options;
  if (!userId || game.status === GameStatus.CANCELLED) {
    return false;
  }
  const onRatingDay = isGameRatingDayBr(game.gameDate);
  const gameStart = new Date(game.gameDate);
  const gameEnd = game.gameEndDate ? new Date(game.gameEndDate) : gameStart;
  const gameFinished =
    gameEnd < new Date() || game.status === GameStatus.COMPLETED;
  if (!onRatingDay && !gameFinished) {
    return false;
  }
  const isGameOwner = game.userId === userId;
  const isTeamStaff =
    teamUserRole === "ADMIN" || teamUserRole === "OWNER";
  const canEdit = isGameOwner || isTeamStaff;
  return canEdit || Boolean(isParticipant);
}

export type GameTeamRatingsCompletionInput = {
  myTeammateRatingsSummary?: Game["myTeammateRatingsSummary"];
  ratingProgress?: {
    totalToRate: number;
    completed: boolean;
    organizerView?: boolean;
    listedCount?: number;
    ratedCount?: number;
  } | null;
  myGameFeedbackSubmitted?: boolean;
};

/** Participante já concluiu avaliações pendentes (colegas e/ou feedback do jogo). */
export function hasUserCompletedGameTeamRatings(
  input?: GameTeamRatingsCompletionInput | null,
): boolean {
  if (!input) return false;

  const summary = input.myTeammateRatingsSummary;
  if (summary != null && summary.teammatesToRate > 0) {
    return summary.ratingsGiven >= summary.teammatesToRate;
  }

  const progress = input.ratingProgress;
  if (progress?.organizerView) {
    return input.myGameFeedbackSubmitted === true;
  }

  if (progress && progress.totalToRate > 0) {
    return progress.completed;
  }

  const noTeammatesToRate =
    (summary != null && summary.teammatesToRate === 0) ||
    (progress != null && progress.totalToRate === 0);

  if (noTeammatesToRate) {
    return input.myGameFeedbackSubmitted === true;
  }

  return false;
}

/** Exibe ação "Avaliar" — oculta quando o participante já enviou as avaliações. */
export function shouldShowRateTeamAction(
  game: Game,
  options: {
    userId: string | undefined;
    teamUserRole?: string | null;
    isParticipant?: boolean;
  } & GameTeamRatingsCompletionInput,
): boolean {
  if (!canRateTeamForGame(game, options)) {
    return false;
  }

  if (!isGameRatingDayBr(game.gameDate)) {
    return false;
  }

  const isGameOwner = game.userId === options.userId;
  const isTeamStaff =
    options.teamUserRole === "ADMIN" || options.teamUserRole === "OWNER";
  const canEdit = isGameOwner || isTeamStaff;

  // Organizador que não participou: página somente leitura
  if (canEdit && !options.isParticipant) {
    return true;
  }

  if (options.isParticipant && !canEdit) {
    const summary =
      options.myTeammateRatingsSummary ?? game.myTeammateRatingsSummary;
    if ((summary?.teammatesToRate ?? 0) === 0) {
      return false;
    }
  }

  return !hasUserCompletedGameTeamRatings({
    myTeammateRatingsSummary:
      options.myTeammateRatingsSummary ?? game.myTeammateRatingsSummary,
    ratingProgress: options.ratingProgress,
    myGameFeedbackSubmitted: options.myGameFeedbackSubmitted,
  });
}

/** Ação pendente do participante no dia (check-in ou avaliação pós-jogo). */
export type GameParticipantDayAction = {
  kind: "check-in" | "rate";
  actionLabel: string;
  actionIcon: string;
  subPath: "check-in" | "rate-team";
  /** Só para kind check-in: false = janela ainda não abriu. */
  canPerformCheckIn?: boolean;
};

/** Retorna a ação do card quando o usuário tem check-in ou avaliação pendente. */
export function getGameParticipantDayAction(
  game: Game,
  options: {
    userId: string | undefined;
    teamUserRole?: string | null;
    isConfirmedParticipant: boolean;
    checkedInAt?: string | null;
    myTeammateRatingsSummary?: Game["myTeammateRatingsSummary"];
    nowMs?: number;
  },
): GameParticipantDayAction | null {
  if (!options.userId || !options.isConfirmedParticipant) {
    return null;
  }

  const nowMs = options.nowMs ?? Date.now();
  const hasCoordinates =
    game.locationLat != null && game.locationLng != null;
  const checkInInput = {
    eventStartsAt: game.gameDate,
    eventEndsAt: game.gameEndDate,
    hasLocationCoordinates: hasCoordinates,
    checkedInAt: options.checkedInAt,
    nowMs,
  };

  if (
    !options.checkedInAt &&
    shouldShowEventCheckInPrompt(checkInInput)
  ) {
    const canCheckInNow = canPerformEventSelfCheckIn(checkInInput);

    if (canCheckInNow) {
      return {
        kind: "check-in",
        actionLabel: "Fazer check-in",
        actionIcon: "where_to_vote",
        subPath: "check-in",
        canPerformCheckIn: true,
      };
    }

    const windowState = getCheckInWindowState(
      game.gameDate,
      game.gameEndDate,
      nowMs,
    );

    return {
      kind: "check-in",
      actionLabel: formatCheckInOpensIn(windowState.opensAtMs, nowMs),
      actionIcon: "schedule",
      subPath: "check-in",
      canPerformCheckIn: false,
    };
  }

  if (
    shouldShowRateTeamAction(
      {
        ...game,
        myTeammateRatingsSummary:
          options.myTeammateRatingsSummary ?? game.myTeammateRatingsSummary,
      },
      {
        userId: options.userId,
        teamUserRole: options.teamUserRole,
        isParticipant: true,
        myTeammateRatingsSummary: options.myTeammateRatingsSummary,
      },
    )
  ) {
    return {
      kind: "rate",
      actionLabel: "Avaliar",
      actionIcon: "star",
      subPath: "rate-team",
    };
  }

  return null;
}

export const SPORTS_OPTIONS = [
  { value: "BEACH_TENNIS", label: "Beach Tennis", icon: "sports_tennis" },
  { value: "CARIMBA", label: "Carimba", icon: "sports_handball" },
  { value: "CORRIDA", label: "Corrida", icon: "directions_run" },
  { value: "FUTEBOL", label: "Futebol", icon: "sports_soccer" },
  { value: "VOLEI", label: "Vôlei", icon: "sports_volleyball" },
  { value: "OUTRO", label: "Outros", icon: "sports_score" },
] as const;

/**
 * Retorna true quando o jogo efetivamente encerrou.
 * Usa `gameEndDate` quando disponível (permitindo inscrições/ações durante o jogo
 * que ainda está em andamento) e faz fallback em `gameDate` quando não houver fim definido.
 */
export function isGameEnded(
  game: Pick<Game, "gameDate" | "gameEndDate">,
  reference: Date = new Date(),
): boolean {
  const gameDate = new Date(game.gameDate);
  const gameEndDate = game.gameEndDate ? new Date(game.gameEndDate) : null;
  return gameEndDate ? gameEndDate < reference : gameDate < reference;
}

/** Período antes da abertura pública — membros VIP podem se inscrever antecipadamente. */
export function gameHasVipEarlyAccessWindow(
  game: Pick<
    Game,
    "status" | "listType" | "listOpenAt" | "interestStartAt" | "gameDate" | "gameEndDate"
  >,
  reference: Date = new Date(),
): boolean {
  if (game.status === GameStatus.CANCELLED) return false;
  if (isGameEnded(game, reference)) return false;

  if (game.listType === ListType.DIRECT) {
    const listOpenAt = game.listOpenAt ? new Date(game.listOpenAt) : null;
    return listOpenAt != null && reference < listOpenAt;
  }

  if (game.listType === ListType.INTEREST) {
    const interestStartAt = game.interestStartAt
      ? new Date(game.interestStartAt)
      : null;
    return interestStartAt != null && reference < interestStartAt;
  }

  return false;
}

/** Usuário com papel privilegiado ou dono do jogo pode acessar antes da abertura pública. */
export function userHasVipEarlyAccess(
  game: Pick<Game, "userId">,
  options?: { userId?: string; userTeamRole?: string | null },
): boolean {
  if (options?.userId && options.userId === game.userId) return true;
  const role = options?.userTeamRole;
  return role === "ADMIN" || role === "OWNER" || role === "MEMBER_VIP";
}

/** Selo no card: janela VIP ativa e o usuário é VIP, admin ou dono do time/jogo. */
export function shouldShowVipAccessBadge(
  game: Parameters<typeof gameHasVipEarlyAccessWindow>[0] & Pick<Game, "userId">,
  options?: { userId?: string; userTeamRole?: string | null },
  reference?: Date,
): boolean {
  if (!gameHasVipEarlyAccessWindow(game, reference)) return false;
  return userHasVipEarlyAccess(game, options);
}

// Helper para verificar se as vendas/inscrições já começaram
export function canRegister(
  game: Game,
  options?: {
    userId?: string;
    userTeamRole?: string; // 'OWNER' | 'ADMIN' | 'MEMBER' | 'VISITOR'
  },
): boolean {
  const now = new Date();

  // Status CANCELLED não permite inscrição
  if (game.status === GameStatus.CANCELLED) {
    return false;
  }

  // Se o jogo já encerrou, não permite inscrição.
  // Enquanto o jogo estiver em andamento, inscrição/pagamento continuam disponíveis
  // (desde que ainda haja vaga).
  if (isGameEnded(game, now)) {
    return false;
  }

  // Lista Direta
  if (game.listType === ListType.DIRECT) {
    const listOpenAt = game.listOpenAt ? new Date(game.listOpenAt) : null;

    // Se tem data de abertura e ainda não chegou
    if (listOpenAt && now < listOpenAt) {
      // Verificar se o usuário é ADMIN, OWNER, MEMBER_VIP ou dono do jogo
      const isAdminOwnerOrVip =
        options?.userTeamRole === "ADMIN" ||
        options?.userTeamRole === "OWNER" ||
        options?.userTeamRole === "MEMBER_VIP";
      const isGameOwner = options?.userId === game.userId;

      // Só pode se inscrever se for ADMIN, OWNER, MEMBER_VIP ou dono do jogo
      // Mensalista não pode se inscrever antes do prazo
      if (!isAdminOwnerOrVip && !isGameOwner) {
        return false;
      }
    }

    // Se não tem data de abertura ou já passou, pode se inscrever
    return true;
  }

  // Manifestação de Interesse
  if (game.listType === ListType.INTEREST) {
    const interestStartAt = game.interestStartAt
      ? new Date(game.interestStartAt)
      : null;
    const interestEndAt = game.interestEndAt
      ? new Date(game.interestEndAt)
      : null;

    // Se ainda não começou a manifestação
    if (interestStartAt && now < interestStartAt) {
      // Verificar se o usuário é ADMIN, OWNER, MEMBER_VIP ou dono do jogo
      const isAdminOwnerOrVip =
        options?.userTeamRole === "ADMIN" ||
        options?.userTeamRole === "OWNER" ||
        options?.userTeamRole === "MEMBER_VIP";
      const isGameOwner = options?.userId === game.userId;

      // Só pode se inscrever antecipadamente se for ADMIN, OWNER, MEMBER_VIP ou dono do jogo
      if (isAdminOwnerOrVip || isGameOwner) {
        return true;
      }

      // Se não for privilegiado, não pode se inscrever antes do início
      return false;
    }

    // Só pode realizar inscrição em dois momentos:
    // 1. Durante o período de Manifestação de Interesse (interestStartAt <= now < interestEndAt)
    const isInInterestPeriod =
      interestStartAt &&
      interestEndAt &&
      now >= interestStartAt &&
      now < interestEndAt;

    // 2. Após o fim da manifestação (interestEndAt <= now)
    const isAfterInterestEnd = interestEndAt && now >= interestEndAt;

    if (isInInterestPeriod || isAfterInterestEnd) {
      return true;
    }

    // Se ainda não começou ou está fora dos períodos permitidos, não pode se inscrever
    return false;
  }

  // Default: não pode se inscrever
  return false;
}

// Helper para formatar data no formato "Ter, 20 Jan • 20:00 / 22:00" (com gameEndDate se existir)
function brGameTimeParts(iso: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: BR_TIMEZONE,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
}

function partValue(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) {
  return parts.find((p) => p.type === type)?.value ?? "";
}

export function formatGameDate(
  dateString: string,
  endDateString?: string,
): string {
  const parts = brGameTimeParts(dateString);
  const dayName = partValue(parts, "weekday").replace(/\.$/, "");
  const day = partValue(parts, "day");
  const month = partValue(parts, "month").replace(/\.$/, "");
  const hours = partValue(parts, "hour");
  const minutes = partValue(parts, "minute");

  let result = `${dayName}, ${day} ${month} • ${hours}:${minutes}`;

  if (endDateString) {
    const endParts = brGameTimeParts(endDateString);
    result += ` às ${partValue(endParts, "hour")}:${partValue(endParts, "minute")}`;
  }

  return result;
}

// Helper para formatar data de forma visual melhorada (retorna objeto com partes separadas)
export function formatGameDateVisual(
  dateString: string,
  endDateString?: string,
): {
  dayName: string;
  day: string;
  month: string;
  startTime: string;
  endTime?: string;
} {
  const parts = brGameTimeParts(dateString);
  const dayName = partValue(parts, "weekday").replace(/\.$/, "");
  const day = partValue(parts, "day");
  const month = partValue(parts, "month").replace(/\.$/, "");
  const startTime = `${partValue(parts, "hour")}:${partValue(parts, "minute")}`;

  let endTime: string | undefined;
  if (endDateString) {
    const endParts = brGameTimeParts(endDateString);
    endTime = `${partValue(endParts, "hour")}:${partValue(endParts, "minute")}`;
  }

  return {
    dayName,
    day,
    month,
    startTime,
    endTime,
  };
}

// Helper para determinar o status do jogo baseado nas datas
function getGameStatus(game: Game): {
  status: GameCardProps["status"];
  statusLabel: string;
  statusVariant: StatusBadgeVariant;
  actionLabel: string;
  actionVariant: GameCardProps["actionVariant"];
  meta?: string;
  timeLeft?: string;
  countdown?: {
    days: number;
    hours: number;
    minutes: number;
    seconds?: number;
  };
} {
  const now = new Date();

  // Se o jogo já encerrou (usa gameEndDate quando disponível, mantendo o card ativo
  // enquanto o jogo está em andamento)
  if (isGameEnded(game, now)) {
    return {
      status: "full",
      statusLabel: "Encerrado",
      statusVariant: "neutral",
      actionLabel: "Ver Detalhes",
      actionVariant: "secondary",
    };
  }

  // Status CANCELLED
  if (game.status === GameStatus.CANCELLED) {
    return {
      status: "full",
      statusLabel: "Cancelado",
      statusVariant: "danger",
      actionLabel: "Ver Detalhes",
      actionVariant: "secondary",
    };
  }

  // Lista Direta
  if (game.listType === ListType.DIRECT) {
    const listOpenAt = game.listOpenAt ? new Date(game.listOpenAt) : null;

    const paymentDeadline = game.paymentDeadline
      ? new Date(game.paymentDeadline)
      : null;

    // Se ainda não abriu
    if (listOpenAt && now < listOpenAt) {
      return {
        status: "just-added",
        statusLabel: "Aguarde Abertura",
        statusVariant: "info",
        actionLabel: "Ver Detalhes",
        actionVariant: "secondary",
      };
    }

    // Se está aberto e tem prazo de pagamento
    if (paymentDeadline && now < paymentDeadline) {
      const diff = paymentDeadline.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      return {
        status: "payment-due",
        statusLabel: "Pagamento Pendente",
        statusVariant: "warning",
        actionLabel: "Garantir Vaga",
        actionVariant: "primary",
        timeLeft:
          hours > 0
            ? `${hours}h ${minutes}m restantes`
            : `${minutes}m restantes`,
      };
    }

    // Se está aberto (sem prazo de pagamento ou prazo já passou)
    return {
      status: "just-added",
      statusLabel: "Inscrições Abertas",
      statusVariant: "success",
      actionLabel: "Garantir Vaga",
      actionVariant: "primary",
    };
  }

  // Manifestação de Interesse
  if (game.listType === ListType.INTEREST) {
    const interestStartAt = game.interestStartAt
      ? new Date(game.interestStartAt)
      : null;
    const interestEndAt = game.interestEndAt
      ? new Date(game.interestEndAt)
      : null;
    const listRevealAt = game.listRevealAt ? new Date(game.listRevealAt) : null;

    // Se ainda não começou
    if (interestStartAt && now < interestStartAt) {
      return {
        status: "just-added",
        statusLabel: "Aguarde Início",
        statusVariant: "info",
        actionLabel: "Ver Detalhes",
        actionVariant: "secondary",
      };
    }

    // Se está aberto para manifestação
    if (
      interestStartAt &&
      interestEndAt &&
      now >= interestStartAt &&
      now < interestEndAt
    ) {
      return {
        status: "interest-open",
        statusLabel: "Interesse Aberto",
        statusVariant: "success",
        actionLabel: "Ver Detalhes",
        actionVariant: "secondary",
        meta: "Enchendo",
      };
    }

    // Se já revelou a lista
    if (listRevealAt && now >= listRevealAt) {
      return {
        status: "payment-due",
        statusLabel: "Pagamento Pendente",
        statusVariant: "warning",
        actionLabel: "Garantir Vaga",
        actionVariant: "primary",
      };
    }
  }

  // Default
  return {
    status: "just-added",
    statusLabel: "Disponível",
    statusVariant: "info",
    actionLabel: "Ver Detalhes",
    actionVariant: "secondary",
  };
}

// Converter Game para GameCardProps
export function gameToCardProps(
  game: Game,
  onAction?: () => void,
  currentPlayers?: number,
  isRegistered?: boolean,
  userParticipantStatus?: string | null, // Status do participante do usuário (CONFIRMED, PENDING_PAYMENT, WAITING)
  paymentExpiresAt?: string | null, // Data de expiração do pagamento (se houver)
  viewDetailsLabel?: string, // Label quando o botão leva aos detalhes (ex: "Continuar" na home)
  vipAccessOptions?: { userId?: string; userTeamRole?: string | null },
): GameCardProps {
  const statusInfo = getGameStatus(game);
  const participants = currentPlayers ?? 0;
  const remainingSpots =
    game.maxParticipants === 0
      ? Infinity
      : Math.max(0, game.maxParticipants - participants);
  const isFull = game.maxParticipants > 0 && remainingSpots === 0;

  // Imagem: capa do jogo > foto do time > padrão
  const image =
    (game as { coverImage?: string }).coverImage ||
    game.team?.photo ||
    "https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=2093&auto=format&fit=crop";

  // Ajustar status: Se o usuário está inscrito, mostrar o estado do usuário
  // Caso contrário, mostrar o estado normal do jogo
  let finalStatus = statusInfo.status;
  let finalStatusLabel = statusInfo.statusLabel;
  let finalStatusVariant = statusInfo.statusVariant;

  // Se o usuário está inscrito, mostrar o estado do usuário
  if (isRegistered) {
    if (WAITING_STATUSES.includes((userParticipantStatus ?? "") as any)) {
      finalStatus = "full";
      finalStatusLabel = "Lista de Espera";
      finalStatusVariant = "orange";
    } else if (
      PENDING_PAYMENT_STATUSES.includes((userParticipantStatus ?? "") as any)
    ) {
      finalStatus = "payment-due";
      finalStatusLabel = "Pagamento Pendente";
      finalStatusVariant = "warning";

      // Se tiver data de expiração, calcular tempo restante
      if (paymentExpiresAt) {
        const expiresAt = new Date(paymentExpiresAt);
        const now = new Date();
        if (expiresAt > now) {
          const diff = expiresAt.getTime() - now.getTime();
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

          statusInfo.timeLeft =
            hours > 0
              ? `${hours}h ${minutes}m restantes`
              : `${minutes}m restantes`;

          // Adiciona countdown para atualização em tempo real
          statusInfo.countdown = {
            days: 0,
            hours,
            minutes,
            seconds: Math.floor((diff % (1000 * 60)) / 1000),
          };
        }
      }
    } else if (userParticipantStatus === "CONFIRMED") {
      finalStatus = "just-added";
      finalStatusLabel = "Inscrito";
      finalStatusVariant = "success";
    }
  } else if (statusInfo.status === "payment-due") {
    // Só mostrar "Pagamento Pendente" se o usuário está inscrito com PENDING_PAYMENT
    if (
      !isRegistered ||
      !PENDING_PAYMENT_STATUSES.includes((userParticipantStatus ?? "") as any)
    ) {
      // Se está cheio, mostrar status normal (não payment-due)
      if (isFull) {
        if (!game.allowWaitlist && game.listType === ListType.DIRECT) {
          finalStatus = "full";
          finalStatusLabel = "Esgotado";
          finalStatusVariant = "danger";
        } else {
          finalStatus = "full";
          finalStatusLabel = "Cheio";
          finalStatusVariant = "neutral";
        }
      } else {
        // Se não está cheio, mostrar status de inscrições abertas
        finalStatus = "just-added";
        finalStatusLabel = "Inscrições Abertas";
        finalStatusVariant = "success";
      }
    }
  } else if (!isRegistered && isFull) {
    if (!game.allowWaitlist && game.listType === ListType.DIRECT) {
      finalStatus = "full";
      finalStatusLabel = "Esgotado";
      finalStatusVariant = "danger";
    } else if (game.listType === ListType.DIRECT) {
      finalStatus = "full";
      finalStatusLabel = "Lista de Espera";
      finalStatusVariant = "neutral";
    } else if (game.listType === ListType.INTEREST) {
      // Jogos de interesse cheios sempre têm lista de espera
      finalStatus = "full";
      finalStatusLabel = "Lista de Espera";
      finalStatusVariant = "neutral";
    }
  }

  const viewLabel = viewDetailsLabel ?? "Ver Detalhes";
  // Se o usuário está inscrito, mudar o botão para viewLabel com estilo primary
  let actionLabel = isFull ? viewLabel : (statusInfo.actionLabel === "Ver Detalhes" ? viewLabel : statusInfo.actionLabel);
  let actionVariant: GameCardProps["actionVariant"] = isFull
    ? "primary"
    : statusInfo.actionVariant;

  if (isRegistered && !isFull) {
    actionLabel = viewLabel;
    actionVariant = "primary";
  }

  // Calcular countdown se necessário
  // Countdown só deve aparecer quando ainda não abriu a lista/interesse
  let countdown: GameCardProps["countdown"] = undefined;
  const now = new Date();
  const gameEnded = isGameEnded(game, now);

  const buildCountdown = (target: Date): GameCardProps["countdown"] | undefined =>
    buildCardCountdown(target, now);

  if (game.listType === ListType.DIRECT) {
    // Para lista direta: countdown até listOpenAt (quando a lista abre)
    const listOpenAt = game.listOpenAt ? new Date(game.listOpenAt) : null;
    if (listOpenAt && now < listOpenAt) {
      countdown = buildCountdown(listOpenAt);
    }
  } else if (game.listType === ListType.INTEREST) {
    // Para interesse: countdown até interestStartAt (quando a manifestação de interesse começa)
    // NÃO mostrar countdown para listRevealAt
    const interestStartAt = game.interestStartAt
      ? new Date(game.interestStartAt)
      : null;

    if (interestStartAt && now < interestStartAt) {
      countdown = buildCountdown(interestStartAt);
    }
  }

  // Determinar meta: não mostrar "vagas restantes" se o jogo já encerrou
  let meta: string | undefined = undefined;
  if (!gameEnded) {
    if (isFull) {
      if (!game.allowWaitlist && game.listType === ListType.DIRECT) {
        meta = "Esgotado";
      } else {
        meta = "Lista de Espera Aberta";
      }
    } else {
      meta =
        statusInfo.meta ||
        (game.maxParticipants > 0 && remainingSpots > 0
          ? `${remainingSpots} vagas restantes`
          : undefined);
    }
  } else {
    // Se o jogo já encerrou, usar apenas o meta do statusInfo se existir
    meta = statusInfo.meta;
  }

  return {
    id: game.id,
    title: game.name,
    sport: game.sport,
    sportCategoryLabel: getSportCategoryLabel(
      game.sport,
      game.sportCategory ?? null,
    ),
    image,
    date: formatGameDate(game.gameDate, game.gameEndDate),
    location: game.location,
    status: finalStatus,
    statusLabel: finalStatusLabel,
    statusVariant: finalStatusVariant,
    currentPlayers: participants,
    maxPlayers: game.maxParticipants,
    actionLabel,
    actionVariant,
    meta,
    timeLeft: statusInfo.timeLeft,
    countdown,
    teamName: game.team?.name,
    teamId: game.teamId,
    teamSlug: game.team?.slug,
    teamPhoto: game.team?.photo,
    onAction,
    // Mostrar para o usuário o valor que ele efetivamente paga no PIX
    price: getChargedAmount(
      game.pricePerPlayer,
      "PIX",
      (game as any).passFeesToUser ?? true,
    ),
    showVipAccess: shouldShowVipAccessBadge(game, vipAccessOptions),
  };
}

export const getSportTheme = (sport: string | undefined, text: string) => {
  // Check explicit sport field first
  if (sport === "VOLEI")
    return {
      icon: "sports_volleyball",
      label: "Vôlei",
      blob1: "bg-amber-400/20",
      blob2: "bg-yellow-600/20",
      text: "text-amber-700 dark:text-amber-500",
      bg: "bg-amber-500/10",
    };
  if (sport === "BEACH_TENNIS")
    return {
      icon: "sports_tennis",
      label: "Beach Tennis",
      blob1: "bg-orange-400/20",
      blob2: "bg-yellow-400/20",
      text: "text-orange-600 dark:text-orange-400",
      bg: "bg-orange-500/10",
    };
  if (sport === "CARIMBA")
    return {
      icon: "sports_handball",
      label: "Carimba",
      blob1: "bg-purple-500/20",
      blob2: "bg-pink-400/20",
      text: "text-purple-600 dark:text-purple-400",
      bg: "bg-purple-500/10",
    };
  if (sport === "CORRIDA")
    return {
      icon: "directions_run",
      label: "Corrida",
      blob1: "bg-sky-400/20",
      blob2: "bg-blue-400/20",
      text: "text-sky-600 dark:text-sky-400",
      bg: "bg-sky-500/10",
    };
  if (sport === "FUTEBOL")
    return {
      icon: "sports_soccer",
      label: "Futebol",
      blob1: "bg-emerald-500/20",
      blob2: "bg-green-400/20",
      text: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-500/10",
    };

  // Fallback to text matching
  const t = text.toLowerCase();
  if (t.includes("volei") || t.includes("vôlei"))
    return {
      icon: "sports_volleyball",
      label: "Vôlei",
      blob1: "bg-amber-400/20",
      blob2: "bg-yellow-600/20",
      text: "text-amber-700 dark:text-amber-500",
      bg: "bg-amber-500/10",
    };
  if (t.includes("beach") || t.includes("tennis") || t.includes("tenis"))
    return {
      icon: "sports_tennis",
      label: "Beach Tennis",
      blob1: "bg-orange-400/20",
      blob2: "bg-yellow-400/20",
      text: "text-orange-600 dark:text-orange-400",
      bg: "bg-orange-500/10",
    };
  if (t.includes("carimba") || t.includes("queimada") || t.includes("baleado"))
    return {
      icon: "sports_handball",
      label: "Carimba",
      blob1: "bg-purple-500/20",
      blob2: "bg-pink-400/20",
      text: "text-purple-600 dark:text-purple-400",
      bg: "bg-purple-500/10",
    };
  if (t.includes("corrida") || t.includes("run"))
    return {
      icon: "directions_run",
      label: "Corrida",
      blob1: "bg-sky-400/20",
      blob2: "bg-blue-400/20",
      text: "text-sky-600 dark:text-sky-400",
      bg: "bg-sky-500/10",
    };
  if (t.includes("fut") || t.includes("soccer") || t.includes("bola"))
    return {
      icon: "sports_soccer",
      label: "Futebol",
      blob1: "bg-emerald-500/20",
      blob2: "bg-green-400/20",
      text: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-500/10",
    };

  return {
    icon: "sports_score",
    label: "Outros",
    blob1: "bg-muted/20",
    blob2: "bg-muted-foreground/15",
    text: "text-muted-foreground",
    bg: "bg-muted/30",
  };
};
